<template>
  <amf-form :title="title || 'History'">
    <div class="container-fluid full-height flex-column">
      <history-filters ref="filters" :entity-type="entityType" @search="search" />
      <div class="row scroll-y">
        <div v-if="!ready" class="col-12">
          Loading...
        </div>
        <div v-else class="col-12">
          <div v-if="items.length == 0">No history items</div>
          <table class="fixed-table-header table table-sm table-condensed table-hover" v-if="items.length > 0">
            <thead>
              <tr>
                <th v-if="showLinkColumn" width="10%">EE / ER</th>
                <th width="5%">Action</th>
                <th>Description</th>
                <th width="12%">
                  <a @click="toggleSort" class="d-flex flex-row">
                    <div>Changed&nbsp;</div>
                    <div v-if="sortOrder=='desc'" class="fa fa-chevron-down"></div>
                    <div v-else class="fa fa-chevron-up"></div>
                  </a>
                </th>
                <th v-if="showUserColumn" width="10%">User</th>
              </tr>
            </thead>
            <tbody>
            <tr v-for="(item, index) in itemsToDisplay" :key="item.key + index" :class="{ cursor: isClickable(item) }"
                @click.prevent="ignoreOnHighlight(() => showItemDetails(item))">
              <td v-if="showLinkColumn" class="truncated" style="max-width: 250px;">
                <a href="#" v-if="item.entityLink" @click.stop.prevent="openLink(item.entityLink)">
                  {{item.entityContext}}
                </a>
              </td>
              <td>
                <span v-if="item.actionItemLink">
                  <a href="#" @click.stop.prevent="openLink(item.actionItemLink)" title="Open Action Item" style="text-underline: none">
                    <i class="fa fa-list-alt"/>
                  </a>
                </span>
                <span v-if="item.bag.storageFileId">
                  <i
                    @click.stop.prevent="downloadFile(item)"
                    title="Download file"
                    class="fa fa-paperclip"
                    style="display:inline-block;cursor:pointer;font-size:16px;transform: rotate(135deg)">
                  </i>
                </span>
              </td>
              <td class="data-hj-suppress truncated" style="max-width: 800px">{{item.description }}</td>
              <td>{{$formatters.dateTime(item.changeDate)}}</td>
              <td v-if="showUserColumn">{{item.userName}}</td>
            </tr>
            </tbody>
          </table>
          <label v-if="records == 1">{{records}} record</label>
          <label v-else-if="records <= recordsPerPage && records > 0">{{records}} records</label>
          <label v-else-if="records > 0" >{{recordsPerPage}} records. More history records available. Use Search button to narrow down your search.</label>
        </div>
      </div>
    </div>
  </amf-form>
</template>

<script>
import { modalService, romeApi, toastService } from '../framework';
import HistoryFilters from './HistoryFilters.vue'
import HistoryDetail from "./modals/HistoryDetail.vue";
import {ignoreOnHighlight} from "../framework/utils/utils";
import BulkInteractionUpload from "./modals/BulkInteractionUpload";

export default {
  props: {
    participantId: {
      type: String,
      required: false
    },
    employerId: {
      type: String,
      required: false
    },
    userId: {
      type: String,
      required: false
    },
    title: {
      type: String,
      required: false
    },
    showUserColumn: {
      type: Boolean,
      required: false,
      default: true
    },
    showLinkColumn: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: { HistoryFilters, HistoryDetail },
  routeQuery: ['sortOrder'],
  data() {
    return {
      items: [],
      itemsToDisplay: [],
      showLabel: true,
      ready: false,
      sortOrder: "desc",
      records: 0,
      recordsPerPage: 100,
      amfOptions: { noTracking: true },
    }
  },
  computed: {
    entityType() {
      if(this.participantId) return 'Participant';
      if(this.employerId) return 'Employer';
      if(this.userId) return 'User';
    },
    includeBusyIndicator(){
      return !this.participantId && !this.employerId;
    },
    menuItems() {
      var result = ["Copy"];
      if (this.participantId || this.employerId) result.push("Add", "Upload Bulk Interactions", "Download Blank Bulk Interactions Form");
      return result;
    },
  },
  async created() {
    this.employerId = this.$route.params.employerId;
    this.participantId = this.$route.params.participantId;
    this.ready = true;
    this.busy = false;
  },
  methods: {
    ignoreOnHighlight,
    onRouteQueryUpdate() {
      this.$refs.filters.search();
    },
    validateRouteQuery() {
      return !v.invalidComponent(this);
    },
    openLink(link) {
      if (link instanceof Function) {
        link();
      } else {
        location.href = link;
      }
    },
    copy() {
      const el = document.createElement('textarea');

      el.value = this.items.map(x => x.description).join('\r\n');
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-99999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    },
    extendWithLinks(item) {
      if (item.eventType == 'ActionItem') {
        item.actionItemLink = () => {
          this.$store.dispatch("actionItems/search", {
            actionItemId: item.key,
            includeCompletedItems: true,
            includeFutureItems: true,
            includeParticipantItems: true
          })
        }
      }

      if (item.entityType == 'Employer') {
        item.entityLink = `/#/employer/${item.entityId}/general`;
      } else if (item.entityType == 'Participant') {
        item.entityLink = `/#/participant/${item.entityId}/general`;
      }
    },
    toggleSort() {
      this.sortOrder = this.sortOrder == "desc" ? "asc" : "desc";
    },
    downloadFile(item) {
      romeApi.download(`/StorageFile/${item.bag.storageFileId}`);
    },
    isClickable(item){
      return !!item.bag.isAgentless;
    },
    quickSearch(quickSearchText) {
        var itemsToDisplay = this.items;

        if(!quickSearchText) {
            this.itemsToDisplay = itemsToDisplay;
            return;
        }

        var val = quickSearchText.toLowerCase();
        itemsToDisplay = itemsToDisplay.filter(item => {
            var foundMatch = false;
            var fields = [item.userName, item.description, item.entityContext, item.entityId];

            if(item.changeDate && valueContainsDate(item.changeDate, val))
                return true;

            fields.forEach(field => {
                if(!field) return;
                field = field.toLowerCase();

                if(field.includes(val)) {
                    foundMatch = true;
                    return;
                }
            });

            return foundMatch;
        });

        this.itemsToDisplay = itemsToDisplay;

        function valueContainsDate(date, val) {
            var convertedDate = new Date(date).toLocaleDateString("en-US", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
            })

            if(convertedDate.includes(val)) return true;
        }
    },
    async getDataFromServer(filters) {
        this.busy = true;
        let searchData = {
            order: this.sortOrder,
            start: 0,
            length: this.recordsPerPage,
            ...filters
        };

        var url = null;

        configureUrlForEntity(this.$props, 'participant', 'participantId');
        configureUrlForEntity(this.$props, 'employer', 'employerId', 500);
        configureUrlForEntity(this.$props, 'user', 'userId', 50);

        if (!url) throw "Invalid entity type";

        var items = await romeApi.get(url, searchData);
        items.forEach(this.extendWithLinks);

        this.records = items.length;
        this.itemsToDisplay =  items;
        this.items = items;

        this.busy = false;

        function configureUrlForEntity(data, entityType, key, recordsPerPage = null) {
            var entityId = data[key];
            if(!entityId) return;

            url = `/${entityType}/${entityId}/history`;
            searchData[key] = entityId;
            if(recordsPerPage) searchData.length = recordsPerPage;
        }
    },
    async search(filters) {
        if(v.invalidComponent(this)) return;
        if(!filters) return;

        this.latestFilters = filters;

        if(filters.firstLoad || !filters.quickSearchText)
            await this.getDataFromServer(filters);

        if(filters.quickSearchText)
            this.quickSearch(filters.quickSearchText);
    },
    async showItemDetails(item) {
      let res = await modalService.show("history-detail", HistoryDetail, { item, showUser: this.showUserColumn });

      if (res && res.triggerRefresh) {
        await this.search(this.latestFilters);
      }
    },
    async add() {
      var description = await modalService.input("Description", "", "Add new history record");
      if (!description) return;

      var data = { description: description };
      if (this.participantId) {
        await romeApi.post(`participant/${this.participantId}/history`, data);
      } else {
        await romeApi.post(`employer/${this.employerId}/history`, data);
      }

      await this.search(this.latestFilters);

      toastService.success("History record has been added");
    },
    async downloadBlankBulkInteractionsForm() {
      await romeApi.download(`interaction/bulk/download`);
    },
    async uploadBulkInteractions(){
      await modalService.show("bulk-interaction-upload", BulkInteractionUpload, {employerId: this.employerId });
    }
  },
  validations() {
    return {
      noop: v.build(v.noop()),
    }
  }
}

</script>

<style lang="scss" scoped>
.row-display {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 30ch;
}
.custom-control-input:checked~.custom-control-label::before,
.custom-control-input:disabled~.custom-control-label::before {
  border: none;
}

.cursor{
  cursor: pointer;
}

.truncated{
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

</style>



// WEBPACK FOOTER //
// src/history/History.vue